import fuzzysort from 'fuzzysort';
import {isString, isUndefined} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {
  isSearchModifierToken,
  isValidSearchModifierToken,
  SearchQueryPart,
  splitSearchQuery,
} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {HighlightRangeModel} from '../../../../../models/api/conversationHighlightModel';
import {searchTokenBaseStyles} from '../../../../common/searchBar/searchBar';
import {Highlight} from '../../../cells/header/search/highlight/highlight';
import {InboxesSearchSuggestionItemProps} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Props.
 */

interface SearchSuggestionsDropdownItemContentProps extends InboxesSearchSuggestionItemProps {
  isHighlighted: boolean;
  shouldHighlightSearchTokens?: boolean;
}

/*
 * Style.
 */

const StyledSearchSuggestionDropdownItemContentDiv = styled.div`
  grid-area: content;

  gap: 4px;
  ${flexCenter}

  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.normal};

  max-width: 100%;
  overflow: hidden;
`;

const StyledSearchSuggestionDropdownItemContentPartDiv = styled.div`
  ${flexCenter}
  ${ellipsis(false)};
  white-space: pre;

  &:first-of-type {
    flex-shrink: 0;
  }
`;

const StyledSearchSuggestionsDropdownItemValuePlaceholderSpan = styled.span`
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledSearchTokenDiv = styled.div<{$shouldHighlight: boolean}>`
  ${searchTokenBaseStyles}
  background-color: ${(p) => p.theme.alphas[p.$shouldHighlight ? 'black40' : 'black30']};

  ${ellipsis()};
`;

/*
 * Component.
 */

// @TODO: Support multiple fuzzy queries (to match `modifier:value`).
export const SearchSuggestionsDropdownItemContent: FC<SearchSuggestionsDropdownItemContentProps> = (
  props,
) => {
  const {
    label,
    value,
    valuePlaceholder,
    fuzzyQuery,
    isHighlighted,
    shouldHighlightSearchTokens,
    ...otherProps
  } = props;
  const shouldHighlight = Boolean(isHighlighted && shouldHighlightSearchTokens);

  return (
    <StyledSearchSuggestionDropdownItemContentDiv {...otherProps}>
      {maybeRenderContentPart(label, undefined, fuzzyQuery, shouldHighlight)}
      {maybeRenderContentPart(value, valuePlaceholder, fuzzyQuery, shouldHighlight)}
    </StyledSearchSuggestionDropdownItemContentDiv>
  );
};

/*
 * Helpers.
 */

export function maybeRenderContentPart(
  part: ReactNode | undefined,
  partPlaceholder: ReactNode | undefined,
  query: string | undefined,
  shouldHighlight: boolean,
): ReactNode {
  if (isUndefined(part)) {
    return null;
  }

  if (!isString(part)) {
    return (
      <StyledSearchSuggestionDropdownItemContentPartDiv>
        {part}
      </StyledSearchSuggestionDropdownItemContentPartDiv>
    );
  }

  if (!isUndefined(partPlaceholder)) {
    // Compute highlight ranges for the part only.
    const highlightRanges = computeHighlightRanges(part, query);

    return (
      <StyledSearchSuggestionDropdownItemContentPartDiv>
        <StyledSearchTokenDiv $shouldHighlight={shouldHighlight}>
          <Highlight text={part} ranges={highlightRanges} />
          <StyledSearchSuggestionsDropdownItemValuePlaceholderSpan>
            {partPlaceholder}
          </StyledSearchSuggestionsDropdownItemValuePlaceholderSpan>
        </StyledSearchTokenDiv>
      </StyledSearchSuggestionDropdownItemContentPartDiv>
    );
  }

  return (
    <StyledSearchSuggestionDropdownItemContentPartDiv>
      {renderHighlightedLabel(part, query, shouldHighlight)}
    </StyledSearchSuggestionDropdownItemContentPartDiv>
  );
}

function renderHighlightedLabel(str: string, query: string | undefined, shouldHighlight: boolean) {
  // Compute highlight ranges for the whole string.
  const highlightRanges = computeHighlightRanges(str, query);

  const mergedQueryParts = splitAndMergeQueryParts(str);

  return mergedQueryParts.map(({token, text, startOffset, endOffset}) => {
    // Find highlight ranges that belongs to this query part and update them.
    const relatedHighlightRanges = highlightRanges
      .filter(({start, length}) => start >= startOffset && start + length <= endOffset)
      .map(({start, length}) => ({
        start: start - startOffset,
        length,
      }));

    if (!token || !isSearchModifierToken(token) || !isValidSearchModifierToken(token)) {
      return <Highlight key={startOffset} text={text} ranges={relatedHighlightRanges} />;
    }

    return (
      <StyledSearchTokenDiv key={startOffset} $shouldHighlight={shouldHighlight}>
        <Highlight text={text} ranges={relatedHighlightRanges} />
      </StyledSearchTokenDiv>
    );
  });
}

function computeHighlightRanges(text: string, query: string | undefined): ReadonlyArray<HighlightRangeModel> {
  if (!query) {
    return [];
  }

  const [result] = fuzzysort.go(query, [text]);
  const fuzzyIndexes = result?.indexes;

  if (!fuzzyIndexes || fuzzyIndexes.length === 0) {
    return [];
  }

  return fuzzyHighlightToHighlightRanges(fuzzyIndexes);
}

/**
 * Transform an array of indexes to highlight : [1, 2, 3, 5, 6, 8] (fuzzysort library format)
 * into an array of highlight ranges: [{start: 1,length: 3},{start: 5,length: 2},{start: 8,length: 1}] (<Highlight> component format)
 */
export function fuzzyHighlightToHighlightRanges(
  fuzzyIndexes: Fuzzysort.Result['indexes'],
): ReadonlyArray<HighlightRangeModel> {
  // Init
  const highlightRanges: Array<HighlightRangeModel> = [];

  let currentIndex = 0;
  let start = fuzzyIndexes[currentIndex];
  let length = 1;
  while (currentIndex < fuzzyIndexes.length) {
    // Increment length while next value is equal to current value + 1
    while (fuzzyIndexes[currentIndex] + 1 === fuzzyIndexes[++currentIndex]) {
      length++;
    }

    // Push the new highlight range
    highlightRanges.push({start, length});

    // Init next highlight range
    start = fuzzyIndexes[currentIndex];
    length = 1;
  }

  return highlightRanges;
}

export function splitAndMergeQueryParts(str: string): ReadonlyArray<SearchQueryPart> {
  const queryParts = splitSearchQuery(str);

  const mergedQueryParts: Array<SearchQueryPart> = [];

  // For each query part, check if we could merge with the previous one.
  for (const queryPart of queryParts) {
    const {token, text, startOffset, endOffset} = queryPart;

    // If we have a modifier token, we never want to merge.
    if (token && isSearchModifierToken(token)) {
      mergedQueryParts.push(queryPart);
      continue;
    }

    const lastMergedQueryPartIndex = mergedQueryParts.length - 1;
    const previousMergedQueryPart = mergedQueryParts[lastMergedQueryPartIndex];

    // If the previous query part does not exist or contains a modifier token, add the query part to the array.
    if (
      !previousMergedQueryPart ||
      (previousMergedQueryPart.token && isSearchModifierToken(previousMergedQueryPart.token))
    ) {
      mergedQueryParts.push({
        text,
        startOffset,
        endOffset,
        // We don't care about the token as we're using `text` for simple tokens.
        token: undefined,
      });
      continue;
    }

    // Otherwise, merge the current query part with the previous one.
    mergedQueryParts[lastMergedQueryPartIndex] = {
      // Concatenate texts.
      text: previousMergedQueryPart.text + text,
      // Keep the original start offset.
      startOffset: previousMergedQueryPart.startOffset,
      // Update the end offset
      endOffset,
      // We don't care about the token as we'll use text.
      token: undefined,
    };
  }

  return mergedQueryParts;
}
